'use client';
import ProductBanner from '@components/ProductBanner';
import Markdown from '@repo/ui/src/components/Markdown';
import RatingStars from '@repo/ui/src/components/RatingStars';
import Rating from '@components/shared/Bazaarvoice/Rating';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import Product from '@models/product';
import { classNames } from '@repo/ui/src/utils/classNames';
import React from 'react';
import ProductPrice from '../Price';

export interface ProductHeadingProps {
  product: Product;
  className?: string;
  showPrice?: boolean;
  dark?: boolean;
  hideDescription?: boolean;
  hideSKU?: boolean;
}

const ProductHeading = ({
  product,
  className,
  showPrice,
  dark,
  hideDescription,
  hideSKU = false,
}: ProductHeadingProps) => {
  const { eCommEnabled, enableBazaarvoice } = useCurrentUrl();

  const showBazaarvoice = enableBazaarvoice && product?.productUrl?.includes('/meater/');
  const hasRatingSummary = !!product.ratingSummary;
  const showRating = eCommEnabled && (showBazaarvoice || hasRatingSummary);

  return (
    <div
      className={classNames(
        'tw-px-6 md:tw-pr-[5vw] md:tw-pl-0',
        dark && 'md:tw-bg-transparent tw-text-white',
        !hideDescription && 'tw-pb-8',
        className,
      )}
    >
      <div className="tw-flex tw-justify-center md:tw-justify-start tw-mb-2 md:tw-mb-5">
        <ProductBanner product={product} />
      </div>
      {product.smallHeading && (
        <p className="tra-text-web-sub-2 tw-uppercase tw-text-center md:tw-text-left">
          {product.smallHeading}
        </p>
      )}
      <h1 className="tra-text-web-heading-2 tw-text-center md:tw-text-left">
        {product.shortTitleMain ? (
          <>
            {product.shortTitleMain} <span>{product.shortTitlePrimary}</span>
          </>
        ) : (
          product.title
        )}
      </h1>
      <div
        className={classNames(
          'tw-flex tw-gap-4 tw-py-2 md:tw-pt-4 md:tw-pb-0 tw-items-center tw-justify-center md:tw-justify-start',
          hideDescription && 'tw-pb-0',
        )}
      >
        {showRating && (
          <a href="#reviews">
            {showBazaarvoice ? (
              <Rating sku={product.sku} enableSEO={false} type="summary" dark={dark} />
            ) : (
              hasRatingSummary && (
                <RatingStars
                  value={product.ratingSummary.average}
                  hideValue
                  reviewCount={product.ratingSummary.count}
                  dark={dark}
                />
              )
            )}
          </a>
        )}
        {!hideSKU && eCommEnabled && (
          <span
            className={classNames(
              'tw-self-stretch tw-border-l',
              dark ? 'tw-border-warm-400' : 'tw-border-neutral-300',
            )}
          />
        )}
        {!hideSKU && eCommEnabled && (
          <p
            itemProp="sku"
            className={classNames(dark ? 'tw-text-warm-400' : 'tw-text-neutral-300')}
          >
            {product.sku}
          </p>
        )}
      </div>
      {!hideDescription && (
        <div className="tw-pt-4">
          <Markdown bodySize="xLarge" className={classNames(dark && 'tw-text-white')}>
            {product.shortDescription}
          </Markdown>
        </div>
      )}
      {showPrice && product.ctaButtonType !== 'noCTA' && (
        <div className="tw-mt-4 tw-flex">
          <ProductPrice
            product={product}
            component="h3"
            className="tra-text-web-heading-4 tw-justify-center md:tw-justify-start"
          />
        </div>
      )}
    </div>
  );
};

export default ProductHeading;
